import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TableHeaderCell from "../table-cell/TableHeaderCell";
import { showApiErrorMessages } from "../../helpers";
import { useQuery } from "react-query";
import { useOrderBy } from "../../hooks/useOrderBy";
import { ApiCommonParams } from "../../api/@types/apiTypes";
import NoteModel from "../../api/models/NoteModel";
import { getNoteState } from "../sessions/helper";
import { useParams } from "react-router-dom";
import {
  indexStudentNotesById,
  removeNotes,
  updateTutorNote,
} from "../../api/endpoints/tutors/notes";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { Menu, Transition } from "@headlessui/react";
import Pagination from "../pagination/Pagination";

export default function TutorStudentNotes({
  refetchUserData,
}: {
  refetchUserData?: any;
}) {
  const LocalBasePath = "page.supervisor.student";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const { orderBy, getSortingDirection, handleSetSortingOrder } = useOrderBy();
  const { id } = useParams();
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const resetCurrentPage = () => {
    setCurrentPage(1);
  };
  const { data: notes, refetch } = useQuery(
    ["studentNotes", id, orderBy, filters, currentPage],
    () => {
      let apiParams: ApiCommonParams = {
        filters,
        orderBy,
        pagination: { pageSize: 10, pageNumber: currentPage },
      };
      return indexStudentNotesById(id ?? "", apiParams);
    },
    {
      select: (response) => {
        let fetchedNotes: NoteModel[] = [];
        if (response.isSuccess) {
          fetchedNotes = NoteModel.fromApiResponse(response.data);
        } else {
          showApiErrorMessages(response);
        }
        const paginationInfo = response.pagination;
        return { data: fetchedNotes, paginationInfo };
      },
    }
  );
  const updateFilterState = (state: string) => {
    setFilters({ state });
  };
  const UpdateNoteSate = async (state: string, note: NoteModel) => {
    const updatedNoteResponse = await updateTutorNote(note.id, {
      session_id: note.session?.id,
      state: state,
    });
    if (updatedNoteResponse.isSuccess) {
      refetch();

      if (refetchUserData != null) refetchUserData();
    }
  };
  const removeNote = async (note: NoteModel) => {
    const removeResponse = await removeNotes(note.id);
    if (removeResponse.isSuccess) refetch();
  };
  useEffect(() => {
    resetCurrentPage();
  }, [filters, orderBy]);
  return (
    <div className="w-full flex flex-col  bg-gray-50 mb-2  min-h-screen   ">
      <div className="w-full py-4 flex px-6 justify-between items-center  bg-white">
        <h2 className="text-gray-900 text-lg leading-8 font-semibold">
          {t("notesList")}
        </h2>
        <div className="flex items-center gap-4">
          <button
            onClick={() => updateFilterState("")}
            className="text-[#306CE4] text-xs "
          >
            {t("all")}
          </button>
          <button
            onClick={() => updateFilterState("passed")}
            className="text-[#306CE4] text-xs "
          >
            {t("passed")}
          </button>
          <button
            onClick={() => updateFilterState("unpassed")}
            className="text-[#306CE4] text-xs "
          >
            {t("unPassed")}
          </button>
        </div>
      </div>
      <div className="flex flex-col justify-between">
        <div className="">
          <div className="w-full h-px bg-gray-200" />
          {notes?.data?.length! > 0 ? (
            <>
              <table
                data-cy="notesTable"
                className="min-w-full divide-y table-fixed  divide-gray-300  "
              >
                <thead className="bg-gray-50">
                  <tr>
                    <TableHeaderCell
                      className="lg:w-[128px] text-justify hidden  lg:table-cell"
                      sortable={true}
                      sortingDirection={getSortingDirection("session_id")}
                      onClick={handleSetSortingOrder("session_id")}
                    >
                      {t("session")}
                    </TableHeaderCell>
                    <TableHeaderCell className="text-justify ">
                      {t("note")}
                    </TableHeaderCell>
                    <TableHeaderCell
                      className="lg:w-[128px] text-justify hidden  lg:table-cell"
                      sortable={true}
                      sortingDirection={getSortingDirection("state")}
                      onClick={handleSetSortingOrder("state")}
                    >
                      {t("status")}
                    </TableHeaderCell>
                  </tr>
                </thead>
                <tbody>
                  {notes?.data.map((note, index) => {
                    return (
                      <tr
                        key={`NoteInfoTable_${index}_note`}
                        className={" border-t "}
                      >
                        <td className={" px-3 py-3.5  hidden  lg:table-cell"}>
                          <h6 className="text-sm text-gray-900">
                            {note?.session?.sessionNumber!}#
                          </h6>
                          <h6 className="text-xs text-gray-500">
                            {note?.created_at?.format("DD/MM/YYYY")}
                          </h6>
                        </td>
                        <td
                          className={"relative py-4 pl-4 sm:pl-6 pr-3 text-sm"}
                        >
                          <div className="flex justify-between items-center  lg:hidden">
                            {getNoteState(note?.status!)}
                            <Menu
                              as="div"
                              className="relative inline-block text-left"
                            >
                              <div>
                                <Menu.Button
                                  data-cy="updateStatus"
                                  className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-brandblue-500"
                                >
                                  <DotsVerticalIcon
                                    className="h-3 w-3"
                                    aria-hidden="true"
                                  />
                                </Menu.Button>
                              </div>
                              <Transition
                                as={"div"}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                                dir={"ltr"}
                              >
                                {/* Arrow */}
                                <div className="z-[100] w-4 h-4 border border-l-0 border-b-0 border-black border-opacity-5 absolute ltr:right-[100%] rtl:left-[100%] -top-[0.75rem] -translate-y-full ltr:rotate-45 rtl:rotate-[225deg] bg-white focus:outline-none" />
                                <Menu.Items className="z-[99] absolute ltr:right-[120%] rtl:left-[120%] -top-[0.5rem] -translate-y-full mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  <div className="p-2 cursor-pointer">
                                    <Menu.Item>
                                      <button
                                        data-cy="updateStatusPassed"
                                        className="w-full"
                                        onClick={() =>
                                          UpdateNoteSate("passed", note)
                                        }
                                      >
                                        <h5>{t("passed")}</h5>
                                      </button>
                                    </Menu.Item>
                                  </div>
                                  <div className="p-2  cursor-pointer">
                                    <Menu.Item>
                                      <button
                                        className="w-full"
                                        data-cy="updateStatusUnPassed"
                                        onClick={() =>
                                          UpdateNoteSate("unpassed", note)
                                        }
                                      >
                                        <h5>{t("unPassed")}</h5>
                                      </button>
                                    </Menu.Item>
                                  </div>
                                  <div className="p-2  cursor-pointer">
                                    <Menu.Item>
                                      <button
                                        className="w-full"
                                        data-cy="updateStatusUnPassed"
                                        onClick={() => removeNote(note)}
                                      >
                                        <h5>{t("delete")}</h5>
                                      </button>
                                    </Menu.Item>
                                  </div>
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          </div>
                          <div className="font-medium text-gray-500">
                            {note.note}
                          </div>
                        </td>
                        <td
                          className={
                            " px-3 py-3.5 text-sm text-gray-500  hidden lg:table-cell"
                          }
                        >
                          {getNoteState(note?.status!)}
                        </td>

                        <td className=" pe-4 h-2 w-2 px-4 text-gray-400 sm:pe-6 ps-3 hidden  lg:table-cell">
                          <Menu
                            as="div"
                            className="relative inline-block text-left"
                          >
                            <div>
                              <Menu.Button
                                data-cy="updateStatus"
                                className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-brandblue-500"
                              >
                                <DotsVerticalIcon
                                  className="h-3 w-3"
                                  aria-hidden="true"
                                />
                              </Menu.Button>
                            </div>
                            <Transition
                              as={"div"}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                              dir={"ltr"}
                            >
                              {/* Arrow */}
                              <div className="z-[100] w-4 h-4 border border-l-0 border-b-0 border-black border-opacity-5 absolute ltr:right-[100%] rtl:left-[100%] -top-[0.75rem] -translate-y-full ltr:rotate-45 rtl:rotate-[225deg] bg-white focus:outline-none" />
                              <Menu.Items className="z-[99] absolute ltr:right-[120%] rtl:left-[120%] -top-[0.5rem] -translate-y-full mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div className="p-2 cursor-pointer">
                                  <Menu.Item>
                                    <button
                                      data-cy="updateStatusPassed"
                                      className="w-full"
                                      onClick={() =>
                                        UpdateNoteSate("passed", note)
                                      }
                                    >
                                      <h5>{t("passed")}</h5>
                                    </button>
                                  </Menu.Item>
                                </div>
                                <div className="p-2  cursor-pointer">
                                  <Menu.Item>
                                    <button
                                      className="w-full"
                                      data-cy="updateStatusUnPassed"
                                      onClick={() =>
                                        UpdateNoteSate("unpassed", note)
                                      }
                                    >
                                      <h5>{t("unPassed")}</h5>
                                    </button>
                                  </Menu.Item>
                                </div>
                                <div className="p-2  cursor-pointer">
                                  <Menu.Item>
                                    <button
                                      className="w-full"
                                      data-cy="updateStatusUnPassed"
                                      onClick={() => removeNote(note)}
                                    >
                                      <h5>{t("delete")}</h5>
                                    </button>
                                  </Menu.Item>
                                </div>
                              </Menu.Items>
                            </Transition>
                          </Menu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : (
            <>
              <div className="flex h-[30vh] justify-center items-end">
                <h1>{t("noresultsfound")}</h1>
              </div>
            </>
          )}
        </div>
        <div className="sticky mt-auto bottom-0">
          <Pagination
            hasNext={!!notes?.paginationInfo?.hasNext}
            hasPrev={!!notes?.paginationInfo?.hasPrev}
            goToPage={(page: number) => setCurrentPage(page)}
            currentPage={Number(notes?.paginationInfo?.currentPageNumber || 0)}
            numberOfPages={Number(notes?.paginationInfo?.pagesCount || 0)}
          />
        </div>
      </div>
    </div>
  );
}
